import { useEffect, useState } from 'react';
import useTranslation from 'next-translate/useTranslation';

/**
 * Live public-transport departures FROM CPH airport station (into the city and
 * Sweden) - the board an ARRIVING traveller wants. Polls its own /api/transport
 * route to refresh. Pass `initialDepartures` (server-fetched) to render real
 * content on first paint for SEO; omit it and the board fetches client-side.
 */
function TransportBoard({ initialDepartures = null }) {
    const { t } = useTranslation('common');
    const [departures, setDepartures] = useState(initialDepartures); // null = loading, [] = none

    useEffect(() => {
        let active = true;

        const load = async () => {
            try {
                const res = await fetch('/api/transport');
                if (!res.ok) throw new Error(res.statusText);
                const data = await res.json();
                if (active) setDepartures(data.departures ?? []);
            } catch {
                // Keep whatever we had; only fall to [] on the first failure.
                if (active) setDepartures((prev) => prev ?? []);
            }
        };

        load();
        const id = setInterval(load, 60 * 1000);
        return () => {
            active = false;
            clearInterval(id);
        };
    }, []);

    // Nothing to show (still loading, or the board is empty/unavailable): render
    // nothing rather than an empty card.
    if (!departures || departures.length === 0) return null;

    return (
        <div className="mx-auto my-8 w-[95%] max-w-md rounded-lg bg-neutral-100 p-5 text-left text-black">
            <div className="mb-3 text-sm font-semibold md:text-base">
                {t('transport_title')}
            </div>
            <ul className="flex flex-col gap-2">
                {departures.map((d, i) => (
                    <li
                        key={`dep-${i}`}
                        className="flex items-center gap-3 rounded bg-white px-4 py-2 drop-shadow"
                    >
                        <span className="min-w-[3rem] shrink-0 rounded bg-neutral-800 px-2 py-0.5 text-center text-xs font-bold text-white">
                            {d.line}
                        </span>
                        <span className="grow truncate font-medium">
                            {d.finalStop}
                        </span>
                        {d.cancelled ? (
                            <span className="shrink-0 text-sm font-semibold text-red-600">
                                {t('transport_cancelled')}
                            </span>
                        ) : (
                            <span className="shrink-0 whitespace-nowrap text-right">
                                <span className="font-semibold">{d.time}</span>
                                {d.timeUntil && (
                                    <span className="ml-2 text-sm opacity-60">
                                        {d.timeUntil}
                                    </span>
                                )}
                            </span>
                        )}
                    </li>
                ))}
            </ul>
            <div className="mt-2 text-right text-xs opacity-50">
                {t('transport_source')}
            </div>
        </div>
    );
}

export default TransportBoard;
